import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Clear, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";

import { InteractionMode, Tree, TreeEnvironmentRef, TreeItem, TreeItemIndex } from "ui/Tree";

import { TabHeader } from "fond/layout";
import BaseItemTitle from "fond/map/Legend/BaseItemTitle";
import { LegendContainer, LegendHeader, LegendInner } from "fond/map/Legend/BaseLegend.styles";
import { LegendButton } from "fond/map/Legend/Legend.styles";
import { generateTreeItemParentMap, getTreeItemSiblings, resolveEntityVisibility } from "fond/map/Legend/visibility";

const isControlKey = (e: React.MouseEvent) => e.ctrlKey || (navigator.platform.toUpperCase().indexOf("MAC") >= 0 && e.metaKey);

interface BaseLegendProps<T> {
  environmentRef: MutableRefObject<TreeEnvironmentRef | undefined>;
  treeId: string;
  treeItems: Record<TreeItemIndex, TreeItem<T>>;
  buttons?: React.ReactNode | null;
  header?: React.ReactNode | null;
  /** Rendered above the scrollable legend content; remains visible while scrolling. */
  stickyHeader?: React.ReactNode | null;
  expand?: {
    onItemExpand?: (item: TreeItem<T>) => void;
    defaultExpanded?: TreeItemIndex[];
  } | null;
  getItemTitle?: (item: TreeItem<T>) => string;
  getItemTitleClass?: (item: TreeItem<T>) => string | undefined;
  renderItemLeftAdornment?: (item: TreeItem<T>) => React.ReactElement | null | string;
  renderItemRightAdornment?: (item: TreeItem<T>) => React.ReactElement | null | string;
  getItemClass?: (item: TreeItem<T>) => string | undefined;
  // While we could manage visibility as state within the legend, map visibility and legend visibility
  // are tightly coupled, and it would be easy for them to get out of sync if managed incorrectly. To avoid this,
  // we instead leave visibility management to the parent component.
  //
  // Note: This function must obey the invariant that if a parent is invisible, then all of its descendants
  //       are invisible.
  getItemVisibility: (item: TreeItemIndex) => boolean;
  onItemVisibilityChanges: (itemsToShow: TreeItemIndex[], itemsToHide: TreeItemIndex[]) => void;
}

const BaseLegend = <T,>({
  environmentRef,
  treeItems,
  treeId,
  getItemTitle,
  getItemTitleClass,
  renderItemLeftAdornment,
  renderItemRightAdornment,
  getItemClass,
  getItemVisibility,
  onItemVisibilityChanges,
  expand,
  header,
  stickyHeader,
  buttons,
}: BaseLegendProps<T>): React.ReactElement => {
  const [selectedItems, setSelectedItems] = useState<TreeItemIndex[]>([]);
  const [focusedItem, setFocusedItem] = useState<TreeItemIndex>();
  const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>(expand?.defaultExpanded ?? []);

  /**
   * Determine if the current selected items are either all on, off or mixed
   */
  const visibilityState: boolean = useMemo(() => {
    const values = selectedItems.map((index) => getItemVisibility(index));
    const hasTrue = values.some((value) => value);
    const hasFalse = values.some((value) => !value);

    return !(hasTrue && !hasFalse) || !hasTrue;
  }, [selectedItems, getItemVisibility]);

  const treeItemParent = useMemo(() => generateTreeItemParentMap(treeItems), [treeItems]);

  const setEntityVisibilities = useCallback(
    (entityVisibility: { [key: string]: boolean }) => {
      const { itemsToShow, itemsToHide } = resolveEntityVisibility(entityVisibility, treeItems, treeItemParent, getItemVisibility);
      onItemVisibilityChanges(itemsToShow, itemsToHide);
    },
    [getItemVisibility, onItemVisibilityChanges, treeItemParent, treeItems]
  );

  const handleOnToggle = useCallback(
    () => setEntityVisibilities(Object.fromEntries(selectedItems.map((key) => [key, visibilityState]))),
    [setEntityVisibilities, selectedItems, visibilityState]
  );

  const handleOnExpand = useCallback(
    (item: TreeItem<T>, _: string) => {
      expand?.onItemExpand?.(item);
      setExpandedItems((prev) => [...prev, item.index]);
    },
    [expand]
  );

  const handleOnCollapse = useCallback(
    (item: TreeItem<T>, _: string) => setExpandedItems((prev) => prev.filter((expandedItemIndex) => expandedItemIndex !== item.index)),
    []
  );
  const handleOnSelect = useCallback((items: TreeItemIndex[], _: string) => setSelectedItems(items), []);
  const handleOnFocus = useCallback((item: TreeItem<T>, _: string) => setFocusedItem(item.index), []);

  /**
   * When legend loses focus deselect items
   */
  const legendRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = () => {
      if (legendRef.current && window.event?.target instanceof Element && !legendRef.current.contains(window.event?.target)) {
        environmentRef.current?.selectItems([], treeId);
      }
    };

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [environmentRef, legendRef, treeId]);

  const getSiblingVisibilities = useCallback(
    (key: string) => {
      const siblings = getTreeItemSiblings(key, treeItems, treeItemParent);
      if (siblings.length === 0) return {};

      const visibleCount = siblings.filter((siblingId) => getItemVisibility(siblingId)).length;
      const shouldShowSiblings = visibleCount < siblings.length / 2;
      return Object.fromEntries(siblings.map((siblingId) => [siblingId, shouldShowSiblings]));
    },
    [treeItemParent, treeItems, getItemVisibility]
  );

  const handleVisibilityToggle = useCallback(
    (key: string, mode: "self" | "siblings") => {
      if (mode === "self") {
        setEntityVisibilities({ [key]: !getItemVisibility(key) });
      } else {
        setEntityVisibilities({ [key]: true, ...getSiblingVisibilities(key) });
      }
    },
    [getItemVisibility, getSiblingVisibilities, setEntityVisibilities]
  );

  const renderItemTitle = useCallback(
    ({ title, item }: { title: string; item: TreeItem<T> }) => {
      const onItemVisibilityToggle = (mode: "self" | "siblings") => handleVisibilityToggle(item.index as string, mode);

      return (
        <BaseItemTitle
          title={title}
          isVisible={getItemVisibility(item.index)}
          className={getItemClass?.(item)}
          onItemVisibilityToggle={onItemVisibilityToggle}
          leftAdornment={renderItemLeftAdornment?.(item)}
          rightAdornment={renderItemRightAdornment?.(item)}
        />
      );
    },
    [getItemClass, getItemVisibility, handleVisibilityToggle, renderItemLeftAdornment, renderItemRightAdornment]
  );

  return (
    <LegendContainer data-testid="legend" ref={legendRef}>
      <LegendHeader>
        <TabHeader
          leftAdornments={
            selectedItems.length > 0 ? (
              <Box sx={{ fontWeight: "normal", color: "text.secondary", whiteSpace: "nowrap" }}>{selectedItems.length} selected</Box>
            ) : (
              buttons
            )
          }
          rightAdornments={
            selectedItems.length > 0 && (
              <>
                <Tooltip title="Clear selection" PopperProps={{ disablePortal: true }}>
                  <LegendButton
                    variant="outlined"
                    size="small"
                    startIcon={<Clear />}
                    onClick={() => environmentRef.current?.selectItems([], treeId)}
                    data-testid="clear-selection-button"
                    sx={{ mr: 0.5 }}
                  >
                    Clear
                  </LegendButton>
                </Tooltip>
                <Tooltip title={`${visibilityState ? "Show" : "Hide"} layers`} PopperProps={{ disablePortal: true }}>
                  <LegendButton
                    variant="outlined"
                    size="small"
                    startIcon={visibilityState ? <Visibility /> : <VisibilityOff />}
                    onClick={handleOnToggle}
                    data-testid="toggle-visibility-button"
                  >
                    {visibilityState ? "Show" : "Hide"}
                  </LegendButton>
                </Tooltip>
              </>
            )
          }
        />
      </LegendHeader>
      {stickyHeader}
      <LegendInner className="legend-inner customScrollbars" data-testid="legend-inner">
        {header}
        <Tree<T>
          autoFocus={false}
          id={treeId}
          ref={environmentRef}
          items={treeItems}
          // We use controlled mode to allow treeItems changes to be
          // reflected in the legend (e.g. Dynamically adding cost to serve layers)
          mode="controlled"
          getItemTitle={getItemTitle}
          getItemClass={getItemTitleClass}
          renderItemTitle={renderItemTitle}
          onCollapseItem={handleOnCollapse}
          onExpandItem={handleOnExpand}
          onSelectItems={handleOnSelect}
          onFocusItem={handleOnFocus}
          viewState={{
            expandedItems,
            selectedItems,
            focusedItem,
          }}
          /**
           * We use a custom interaction mode to allow the user to toggle selection via clicking.
           * The default behaviour is to select on click (not deselect).
           */
          defaultInteractionMode={{
            mode: "custom",
            extends: InteractionMode.ClickArrowToExpand,
            createInteractiveElementProps: (item, id, actions, renderFlags) => ({
              onClick: (e) => {
                actions.focusItem();
                if (e.shiftKey) {
                  actions.selectUpTo(!e.ctrlKey);
                } else if (isControlKey(e)) {
                  if (renderFlags.isSelected) {
                    actions.unselectItem();
                  } else {
                    actions.addToSelectedItems();
                  }
                } else {
                  if (renderFlags.isSelected && environmentRef.current?.viewState[id]?.selectedItems?.length === 1) {
                    actions.unselectItem();
                  } else {
                    actions.selectItem();
                  }

                  if (!item.isFolder || environmentRef.current?.canInvokePrimaryActionOnItemContainer) {
                    actions.primaryAction();
                  }
                }
              },
              onFocus: () => {
                actions.focusItem();
              },
              tabIndex: renderFlags.isFocused ? 0 : -1,
            }),
          }}
        />
      </LegendInner>
    </LegendContainer>
  );
};

export default BaseLegend;
